import React, { useEffect, useState } from "react";
import { ExternalLink, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * AdPlaceholder - Displays promotional and affiliate placements
 * 
 * @param {string} placement - One of: "landing", "free_members_main", "below_video"
 * @param {string} className - Additional CSS classes
 */
export default function AdPlaceholder({ placement = "free_members_main", className = "" }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    const loadAds = async () => {
      try {
        const response = await api.get(`/api/ads-affiliate/public?placement=${placement}`);
        setAds(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to load ads:", error);
        setAds([]);
      } finally {
        setLoading(false);
      }
    };
    loadAds();
  }, [placement]);

  const trackClick = async (adId) => {
    try {
      await api.post("/api/ads-affiliate/track/click", { placement_id: adId });
    } catch (error) {
      console.error("Failed to track ad click:", error);
    }
  };

  const handleDismiss = (adId) => {
    setDismissed(prev => new Set([...prev, adId]));
  };

  const visibleAds = ads.filter(ad => !dismissed.has(ad.placement_id));

  if (loading || visibleAds.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {visibleAds.map((ad) => (
        <AdCard
          key={ad.placement_id}
          ad={ad}
          onDismiss={handleDismiss}
          onTrackClick={trackClick}
        />
      ))}
    </div>
  );
}

function AdCard({ ad, onDismiss, onTrackClick }) {
  const handleClick = () => {
    onTrackClick(ad.placement_id);
    if (ad.destination_url) {
      window.open(ad.destination_url, "_blank", "noopener,noreferrer");
    }
  };

  if (ad.type === "html_embed" && ad.embed_code) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-gray-900/50 backdrop-blur-xl">
        <button
          onClick={() => onDismiss(ad.placement_id)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white/70 hover:text-white transition-all"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        <div 
          className="p-6"
          dangerouslySetInnerHTML={{ __html: ad.embed_code }} 
        />
      </div>
    );
  }

  if (ad.type === "image_banner" && ad.media_url) {
    return (
      <div className="relative group rounded-2xl overflow-hidden border border-white/10 bg-gray-900/50 backdrop-blur-xl hover:border-pink-500/30 transition-all duration-300">
        <button
          onClick={() => onDismiss(ad.placement_id)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white/70 hover:text-white transition-all"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        
        <div 
          onClick={ad.destination_url ? handleClick : undefined}
          className={ad.destination_url ? "cursor-pointer" : ""}
        >
          <div className="relative aspect-[21/9] overflow-hidden bg-gray-800">
            <img
              src={ad.media_url}
              alt={ad.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            {/* Gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            
            {/* Content positioned at bottom of image */}
            {(ad.title || ad.description || ad.destination_url) && (
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                {ad.title && (
                  <h3 className="text-lg font-bold text-white mb-2 drop-shadow-lg">{ad.title}</h3>
                )}
                {ad.description && (
                  <p className="text-sm text-gray-200 mb-4 drop-shadow-lg">{ad.description}</p>
                )}
                {ad.destination_url && (
                  <button
                    onClick={handleClick}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-xl font-semibold transition-all duration-200 text-sm shadow-xl"
                  >
                    <span>{ad.button_label || "Learn More"}</span>
                    <ExternalLink size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (ad.type === "affiliate_link") {
    return (
      <div className="relative rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-900/10 to-purple-900/10 backdrop-blur-xl p-6 hover:border-pink-500/40 transition-all duration-300">
        <button
          onClick={() => onDismiss(ad.placement_id)}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white/70 hover:text-white transition-all"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        
        <div className="flex items-start gap-4">
          {ad.media_url && (
            <div className="flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-gray-800">
              <img
                src={ad.media_url}
                alt={ad.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {ad.title && (
              <h3 className="text-base font-bold text-white mb-1">{ad.title}</h3>
            )}
            {ad.description && (
              <p className="text-sm text-gray-400 mb-3">{ad.description}</p>
            )}
            {ad.destination_url && (
              <a
                href={ad.destination_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onTrackClick(ad.placement_id)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg font-semibold transition-all duration-200 text-sm border border-pink-500/30"
              >
                <span>{ad.button_label || "Check it out"}</span>
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (ad.type === "my_toy") {
    return (
      <div className="relative rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-xl p-6 hover:border-purple-500/50 transition-all duration-300">
        <button
          onClick={() => onDismiss(ad.placement_id)}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-black/30 hover:bg-black/50 text-white/70 hover:text-white transition-all"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
        
        <div className="flex flex-col md:flex-row items-center gap-6">
          {ad.media_url && (
            <div className="flex-shrink-0 w-32 h-32 rounded-2xl overflow-hidden bg-gray-800 shadow-2xl">
              <img
                src={ad.media_url}
                alt={ad.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          )}
          
          <div className="flex-1 text-center md:text-left">
            {ad.title && (
              <h3 className="text-xl font-bold text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text mb-2">
                {ad.title}
              </h3>
            )}
            {ad.description && (
              <p className="text-sm text-gray-300 mb-4">{ad.description}</p>
            )}
            {ad.destination_url && (
              <a
                href={ad.destination_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onTrackClick(ad.placement_id)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all duration-200 shadow-lg shadow-purple-500/30"
              >
                <span>{ad.button_label || "Shop Now"}</span>
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
